import React, { useState } from 'react';
import {
  ScrollView,
  View,
  Alert,
  ActivityIndicator,
  Modal,
  TouchableOpacity,
  Image,
} from 'react-native';
import styled from 'styled-components/native';
import { formatDistanceToNow } from 'date-fns';
import * as ImagePicker from 'expo-image-picker';
import * as Sharing from 'expo-sharing';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '@theme/ThemeProvider';
import { Text, Card, Button } from '@ui';

const Container = styled.View`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Content = styled(ScrollView)`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.base}px;
`;

const UploadCard = styled(Card)`
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xxl}px;
  margin-bottom: ${({ theme }) => theme.spacing.base}px;
  border-style: dashed;
  border-width: 2px;
  border-color: ${({ theme }) => theme.colors.border};
`;

const PreviewCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.spacing.base}px;
  overflow: hidden;
`;

const PreviewImage = styled.Image`
  width: 100%;
  height: 300px;
  background-color: ${({ theme }) => theme.colors.neutral100};
`;

const BabyPreviewImage = styled.Image`
  width: 100%;
  height: 400px;
  background-color: ${({ theme }) => theme.colors.neutral100};
`;

const PlaceholderPreview = styled.View`
  width: 100%;
  height: 300px;
  background-color: ${({ theme }) => theme.colors.brandPrimary}15;
  align-items: center;
  justify-content: center;
`;

const ActionButtons = styled.View`
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.md}px;
  padding: ${({ theme }) => theme.spacing.base}px;
`;

const ProcessingModal = styled(Modal)``;

const ProcessingOverlay = styled.View`
  flex: 1;
  background-color: rgba(0, 0, 0, 0.8);
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xl}px;
`;

const ProcessingCard = styled(Card)`
  width: 100%;
  max-width: 320px;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xl}px;
`;

const ProcessingText = styled(Text)`
  margin-top: ${({ theme }) => theme.spacing.md}px;
  text-align: center;
`;

interface Keepsake {
  id: string;
  originalUri: string;
  uploadedAt: string;
  hasBabyPreview: boolean;
}

const JourneyKeepsakesTab: React.FC = () => {
  const theme = useTheme();
  const [keepsakes, setKeepsakes] = useState<Keepsake[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [showBabyPreview, setShowBabyPreview] = useState(false);
  const [processingImageUri, setProcessingImageUri] = useState<string | null>(null); // Added to track image being processed

  const simulateAIProcessing = (callback: () => void) => {
    setIsProcessing(true);
    setProcessingProgress(0);

    // Simulate processing steps
    const steps = [
      { progress: 20, text: 'Analyzing photo...' },
      { progress: 40, text: 'Processing facial features...' },
      { progress: 60, text: 'Generating baby preview...' },
      { progress: 80, text: 'Applying AI enhancement...' },
      { progress: 100, text: 'Complete!' },
    ];

    let currentStep = 0;

    const interval = setInterval(() => {
      if (currentStep < steps.length) {
        setProcessingProgress(steps[currentStep].progress);
        currentStep += 1;
      } else {
        clearInterval(interval);
        setTimeout(() => {
          setIsProcessing(false);
          setProcessingProgress(0);
          setProcessingImageUri(null); // Clear processing image
          callback();
        }, 500);
      }
    }, 800);
  };

  const pickImage = async () => {
    // Request permission
    const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();

    if (permissionResult.granted === false) {
      Alert.alert('Permission Required', 'Permission to access photos is required!');
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [4, 3],
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      const imageUri = result.assets[0].uri;
      setProcessingImageUri(imageUri); // Store the image URI for processing modal

      const newKeepsake: Keepsake = {
        id: Date.now().toString(),
        originalUri: imageUri,
        uploadedAt: new Date().toISOString(),
        hasBabyPreview: false,
      };

      // Start AI processing simulation
      simulateAIProcessing(() => {
        // Mark as having baby preview after processing
        newKeepsake.hasBabyPreview = true;
        setKeepsakes([newKeepsake, ...keepsakes]);
        setShowBabyPreview(true);
      });
    }
  };

  const shareKeepsake = async (keepsake: Keepsake) => {
    const isAvailable = await Sharing.isAvailableAsync();
    if (isAvailable) {
      // For now, share the original image
      await Sharing.shareAsync(keepsake.originalUri);
    } else {
      Alert.alert('Sharing Not Available', 'Sharing is not available on this device');
    }
  };

  const deleteKeepsake = (id: string) => {
    Alert.alert('Delete Keepsake', 'Are you sure you want to delete this keepsake?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: () => {
          setKeepsakes(keepsakes.filter(k => k.id !== id));
        },
      },
    ]);
  };

  const viewBabyPreview = () => {
    setShowBabyPreview(true);
  };

  return (
    <Container>
      <Content showsVerticalScrollIndicator={false}>
        {/* Info Card */}
        <Card padding="md" style={{ marginBottom: 16 }}>
          <Text variant="titleMedium" color="textPrimary" style={{ marginBottom: 8 }}>
            Save Your Special Moments
          </Text>
          <Text variant="bodyMedium" color="textSecondary">
            Upload ultrasound images or photos. Our AI will generate a preview of how your baby
            might look!
          </Text>
        </Card>

        {/* Upload Button */}
        <TouchableOpacity onPress={pickImage} activeOpacity={0.7}>
          <UploadCard>
            <Icon name="cloud-upload" size={48} color={theme.colors.brandPrimary} />
            <Text variant="titleMedium" color="brandPrimary" style={{ marginTop: 12 }}>
              Upload Photo
            </Text>
            <Text variant="bodySmall" color="textSecondary" style={{ marginTop: 4 }}>
              Tap to select from your gallery
            </Text>
          </UploadCard>
        </TouchableOpacity>

        {/* Uploaded Keepsakes */}
        {keepsakes.length > 0 && (
          <Text variant="title" color="textPrimary" style={{ marginTop: 8, marginBottom: 12 }}>
            Your Keepsakes ({keepsakes.length})
          </Text>
        )}

        {keepsakes.map((keepsake, index) => (
          <PreviewCard key={keepsake.id} padding="none">
            <PreviewImage source={{ uri: keepsake.originalUri }} resizeMode="cover" />
            <View style={{ padding: 16 }}>
              {keepsake.hasBabyPreview && (
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                  <Icon name="auto-awesome" size={20} color={theme.colors.brandPrimary} />
                  <Text variant="caption" color="brandPrimary" style={{ marginLeft: 4 }}>
                    Baby Preview Available
                  </Text>
                </View>
              )}
              <Text variant="titleMedium" color="textPrimary">
                Precious Moment #{keepsakes.length - index}
              </Text>
              <Text variant="bodySmall" color="textSecondary" style={{ marginTop: 4 }}>
                Uploaded {formatDistanceToNow(new Date(keepsake.uploadedAt), { addSuffix: true })}
              </Text>
            </View>
            <ActionButtons>
              {keepsake.hasBabyPreview && (
                <Button
                  variant="primary"
                  style={{ flex: 1 }}
                  iconLeft="face"
                  onPress={viewBabyPreview}
                >
                  Meet Your Baby
                </Button>
              )}
              <Button
                variant="tertiary"
                style={{ flex: 1 }}
                iconLeft="share"
                onPress={() => shareKeepsake(keepsake)}
              >
                Share
              </Button>
              <Button
                variant="tertiary"
                style={{ flex: 1 }}
                iconLeft="delete"
                onPress={() => deleteKeepsake(keepsake.id)}
              >
                Delete
              </Button>
            </ActionButtons>
          </PreviewCard>
        ))}

        {/* Placeholder Preview */}
        {keepsakes.length === 0 && (
          <Card padding="none" style={{ marginBottom: 16 }}>
            <PlaceholderPreview>
              <Icon
                name="photo"
                size={64}
                color={theme.colors.brandPrimary}
                style={{ opacity: 0.3 }}
              />
              <Text
                variant="bodyMedium"
                color="textSecondary"
                style={{ marginTop: 12, opacity: 0.7 }}
              >
                No keepsakes yet
              </Text>
            </PlaceholderPreview>
            <View style={{ padding: 16 }}>
              <Text variant="bodyMedium" color="textSecondary">
                Upload your first ultrasound or photo to generate a baby preview
              </Text>
            </View>
          </Card>
        )}

        <View style={{ height: 24 }} />
      </Content>

      {/* AI Processing Modal */}
      <ProcessingModal visible={isProcessing} transparent animationType="fade">
        <ProcessingOverlay>
          <ProcessingCard padding="none" style={{ overflow: 'hidden' }}>
            {/* Show the uploaded image with overlay */}
            {processingImageUri && (
              <View style={{ width: '100%', height: 400, position: 'relative' }}>
                <Image
                  source={{ uri: processingImageUri }}
                  style={{ width: '100%', height: '100%', resizeMode: 'cover' }}
                />
                {/* Dark overlay */}
                <View
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {/* Red star spinner */}
                  <ActivityIndicator size="large" color={theme.colors.brandPrimary} />
                  <ProcessingText
                    variant="titleMedium"
                    color="textOnPrimary"
                    style={{ marginTop: 16, fontWeight: '600' }}
                  >
                    AI Processing...
                  </ProcessingText>
                  <ProcessingText variant="body" color="textOnPrimary" style={{ marginTop: 8 }}>
                    {processingProgress === 20 && 'Analyzing photo...'}
                    {processingProgress === 40 && 'Processing facial features...'}
                    {processingProgress === 60 && 'Generating baby preview...'}
                    {processingProgress === 80 && 'Applying AI enhancement...'}
                    {processingProgress === 100 && 'Complete!'}
                  </ProcessingText>
                  {/* Progress bar */}
                  <View
                    style={{
                      width: '80%',
                      height: 4,
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      borderRadius: 2,
                      marginTop: 16,
                      overflow: 'hidden',
                    }}
                  >
                    <View
                      style={{
                        width: `${processingProgress}%`,
                        height: '100%',
                        backgroundColor: theme.colors.brandPrimary,
                        borderRadius: 2,
                      }}
                    />
                  </View>
                </View>
              </View>
            )}
          </ProcessingCard>
        </ProcessingOverlay>
      </ProcessingModal>

      {/* Baby Preview Modal */}
      <Modal visible={showBabyPreview} transparent animationType="slide">
        <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
          <ScrollView
            contentContainerStyle={{
              flexGrow: 1,
              justifyContent: 'center',
              alignItems: 'center',
              padding: 16,
            }}
          >
            <Card padding="none" style={{ width: '100%', overflow: 'hidden' }}>
              <BabyPreviewImage
                // eslint-disable-next-line global-require
                source={require('../../../assets/babyimage.png')}
                resizeMode="cover"
              />
            </Card>

            {/* Sparkle icons and title */}
            <View
              style={{ flexDirection: 'row', alignItems: 'center', marginTop: 24, marginBottom: 8 }}
            >
              <Text style={{ fontSize: 20, marginRight: 8 }}>✨</Text>
              <Text
                variant="headline"
                color="textPrimary"
                style={{ fontWeight: 'bold', fontSize: 28 }}
              >
                Meet Your Baby
              </Text>
              <Text style={{ fontSize: 20, marginLeft: 8 }}>⭐</Text>
            </View>

            <Text
              variant="bodyMedium"
              color="textSecondary"
              style={{ marginBottom: 16, fontSize: 16 }}
            >
              Before Birth
            </Text>

            <Text
              variant="bodyMedium"
              color="textSecondary"
              style={{
                textAlign: 'center',
                paddingHorizontal: 32,
                lineHeight: 22,
              }}
            >
              Our AI has created a beautiful preview of your little one. This precious moment is now
              saved to your keepsakes.
            </Text>

            <View style={{ marginTop: 32, width: '100%', paddingHorizontal: 16 }}>
              <Button
                variant="primary"
                fullWidth
                onPress={() => setShowBabyPreview(false)}
                iconLeft="check"
              >
                Got it
              </Button>
            </View>
          </ScrollView>
        </View>
      </Modal>
    </Container>
  );
};

export default JourneyKeepsakesTab;
